import React from 'react';
import styled from 'styled-components';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { IGutterProps, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { Button, IButtonProps } from '../Button/Button';
import { MATERIAL_ICONS } from '../Icon/icons';
import { LinkButton } from '../LinkButton/LinkButton';
import { IconButton } from '../IconButton/IconButton';
import { BUTTON_VARIANTS } from '../Button/buttonVariants';
import { BaseDiv } from '../../common/BaseElements';

export type BackNextButtonSize = typeof SIZES.MEDIUM | typeof SIZES.SMALL;
export type BackNextButtonVariant = typeof BUTTON_VARIANTS.SECONDARY | typeof BUTTON_VARIANTS.LINK_DARK;

interface IBackNextButtonBaseProps {
  size?: BackNextButtonSize;
  showText?: boolean;
  variant?: BackNextButtonVariant;
}
export interface IBackNextButtonProps extends IBackNextButtonBaseProps, IGutterProps, Pick<IButtonProps, 'onClick' | 'constantWidth'> {
  text?: string;
}

export interface IBackAndNextButtonProps extends IBackNextButtonBaseProps, IGutterProps {
  backText?: string;
  nextText?: string;
  onBackClick?: () => void;
  onNextClick?: () => void;
}

interface IBackNextInternalButtonProps extends IBackNextButtonProps, Pick<IButtonProps, 'startIcon' | 'endIcon'> {
  testId: string;
  defaultAction: () => void;
}

const ButtonContainer = styled(BaseDiv)<IGutterProps>`
  ${useGutterAsMarginCss()};
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const BackNextButtonBase: React.FC<IBackNextInternalButtonProps> = ({
  text,
  defaultAction,
  testId,
  size = SIZES.SMALL,
  showText = true,
  onClick,
  startIcon,
  endIcon,
  variant = BUTTON_VARIANTS.SECONDARY,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, testId);
  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => (onClick ? onClick(e) : defaultAction());

  if (showText) {
    return variant === BUTTON_VARIANTS.LINK_DARK ? (
      <LinkButton startIcon={startIcon} endIcon={endIcon} variant={BUTTON_VARIANTS.LINK_DARK} onClick={handleClick} {...generateTestId()} {...rest} size={size}>
        {text}
      </LinkButton>
    ) : (
      <Button startIcon={startIcon} endIcon={endIcon} variant={BUTTON_VARIANTS.SECONDARY} onClick={handleClick} {...generateTestId()} {...rest} size={size}>
        {text}
      </Button>
    );
  }

  return <IconButton variant={variant} icon={startIcon ?? endIcon} onClick={handleClick} size={size} {...generateTestId()} {...rest} />;
};

export const BackButton: React.FC<IBackNextButtonProps> = ({ text = 'Back', ...rest }) => (
  <BackNextButtonBase text={text} startIcon={MATERIAL_ICONS.ArrowBack} testId='BackButton' {...rest} defaultAction={() => history.back()} />
);

export const NextButton: React.FC<IBackNextButtonProps> = ({ text = 'Next', ...rest }) => (
  <BackNextButtonBase text={text} endIcon={MATERIAL_ICONS.ArrowForward} testId='NextButton' {...rest} defaultAction={() => history.forward()} />
);

export const BackAndNextButtons: React.FC<IBackAndNextButtonProps> = ({ backText, nextText, onBackClick, onNextClick, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'BackAndNextButton');
  return (
    <ButtonContainer {...rest} {...generateTestId()}>
      <BackButton text={backText} onClick={onBackClick} {...rest} />
      <NextButton text={nextText} onClick={onNextClick} {...rest} />
    </ButtonContainer>
  );
};
